import React from "react";
import { useTranslation } from "react-i18next";
import { ApiTask, ApiRegistration } from "src/api/types";
import useTaskButton from "src/hooks/api/useTaskButton";
import { formatPrice } from "src/lib/formatters";
import Button from "src/components/Button";
import Card from "src/components/Card";
import Skeleton from "src/components/Skeleton";

interface TaskCardProps {
	task: ApiTask;
	registration?: ApiRegistration;
	draftOnClick?: () => void;
	variant?: "large";
}

const TaskCard = ({ task, variant, registration }: TaskCardProps) => {
	const { t } = useTranslation("tasks", {
		keyPrefix: "task-card",
	});
	const button = useTaskButton(task, registration?.hid);

	return (
		<Card
			to={button?.to}
			className="flex flex-1 flex-col curstor-pointer relative"
			contentClassName={`bg-white flex flex-col flex-1 gap-2 dark:bg-dark-accent bg-white ${
				!variant && "px-6 py-6"
			} ${variant === "large" && "px-6 md:px-10 pt-10 pb-8"}`}
		>
			{task?.unread_messages && task.unread_messages > 0 ? (
				<>
					<strong className="text-xm text-red">
						<i className="fas fa-comments mr-2"></i>
						{t("unread-messages", {
							count: task.unread_messages,
						})}
					</strong>
				</>
			) : (
				false
			)}
			<div className="flex flex-col flex-1">
				{task.company && (
					<small className="text-primary">{task.company.name}</small>
				)}
				<div className="flex gap-2">
					<strong className="leading-6 max-h-[48px] flex overflow-hidden">
						{task?.title}
					</strong>
				</div>
			</div>
			<div className="flex flex-col flex-1 gap-2">
				<p className="mb-0 leading-6 h-[72px] block overflow-hidden">
					{task.excerpt}
				</p>

				<div className="flex items-center justify-between">
					<div className="flex flex-col">
						<span className="text-primary">
							{task.is_home ? t("home") : task?.address?.city}
						</span>
						<span className="text-primary">
							{task?.estimated_duration
								? t("estimated-duration", {
										hour: Math.floor(
											Number(task.estimated_duration)
										),
								  })
								: ""}
						</span>
					</div>
					<strong className="text-xl">
						{formatPrice(task.budget)}
					</strong>
				</div>
			</div>
			{variant === "large" ? (
				<div className="md:max-w-3/4 mt-8 w-full mx-auto flex justify-center items-center flex-col gap-2">
					<Button
						className="flex-1 w-full"
						type={button.type}
						to={button.to}
					>
						{button.label}
					</Button>
				</div>
			) : (
				<div
					className={`
                transition-all flex items-center justify-center rounded-b-md ${button.variant} hover:opacity-80 
                -mx-6 -mb-6 px-8 py-2  mt-2 font-bold
            `}
				>
					{button.label}
				</div>
			)}
		</Card>
	);
};

export const TaskCardSkeleton = () => {
	return (
		<Card
			className="flex-1"
			contentClassName="p-4 flex-1 flex flex-col gap-4"
		>
			<div className="flex gap-4">
				<Skeleton className="w-[54px] h-[54px] rounded-full" />
				<div className="flex flex-col w-full">
					<Skeleton className="w-[40%] mb-2 h-6" />
					<Skeleton className="w-[60%] h-4" />
				</div>
			</div>
			<div className="flex-1">
				<Skeleton className="mb-2 h-4 w-[40%]"></Skeleton>
				<Skeleton className="mb-2 h-4 w-full"></Skeleton>
				<Skeleton className="mb-2 h-4 w-full"></Skeleton>
			</div>
		</Card>
	);
};

export default TaskCard;
